import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/Array/sorting2D.css"
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";


const Sorting2D = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [matrix, setMatrix] = useState([]);
    const [selectedCells, setSelectedCells] = useState([]);
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [moves, setMoves] = useState(0);
    const [message, setMessage] = useState("");
    const [currentPass, setCurrentPass] = useState(0);
    const [expectedNextMove, setExpectedNextMove] = useState(null);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const { darkMode } = useDarkMode();
    const LEVELS = [
    {
        id: 1,
        description: selLanguage
            ? "Εφάρμοσε Bubble Sort σε κάθε γραμμή. Ξεκίνα από την πρώτη γραμμή, σύγκρινε από αριστερά προς τα δεξιά. Όταν τελειώσεις μια γραμμή, πήγαινε στην επόμενη."
            : "Apply Bubble Sort on each row. Start from the first row, compare left to right. When you finish a row, move to the next one.",
        matrix: [
            [5, 2, 8, 1],
            [9, 3, 7, 4],
            [6, 12, 10, 11]
        ],
        sortType: "row"
    },
    {
        id: 2,
        description: selLanguage
            ? "Εφάρμοσε Bubble Sort σε κάθε στήλη. Ξεκίνα από την πρώτη στήλη, σύγκρινε από πάνω προς τα κάτω. Όταν τελειώσεις μια στήλη, πήγαινε στην επόμενη."
            : "Apply Bubble Sort on each column. Start from the first column, compare top to bottom. When you finish a column, move to the next one.",
        matrix: [
            [8, 3, 9, 2],
            [5, 7, 4, 6],
            [1, 11, 10, 12]
        ],
        sortType: "column"
    },
    {
        id: 3,
        description: selLanguage
            ? "Εφάρμοσε Bubble Sort στον πίνακα σαν μία συνεχόμενη λίστα (αριστερά→δεξιά, πάνω→κάτω). Σύγκρινε διαδοχικά στοιχεία από την αρχή."
            : "Apply Bubble Sort on the matrix as a single flattened list (left→right, top→bottom). Compare consecutive elements from the beginning.",
        matrix: [
            [8, 2, 5, 9],
            [1, 7, 3, 6],
            [12, 4, 11, 10]
        ],
        sortType: "flat"
    }
];


    useEffect(() => {
        resetLevel();
    }, [currentLevel]);

    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    const resetLevel = () => {
        const level = LEVELS[currentLevel];
        setMatrix(level.matrix.map(row => [...row]));
        setSelectedCells([]);
        setMoves(0);
        setMessage("");
        setCurrentPass(0);
        setExpectedNextMove(null);
    };

    const resetGame = () => {
        setCurrentLevel(0);
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        resetLevel();
    };

    const getNextExpectedComparison = (currentMatrix, sortType) => {
        const numRows = currentMatrix.length;
        const numCols = currentMatrix[0].length;

        if (sortType === "row") {
            for (let row = 0; row < numRows; row++) {
                for (let col = 0; col < numCols - 1; col++) {
                    if (currentMatrix[row][col] > currentMatrix[row][col + 1]) {
                        return { row, col1: col, col2: col + 1, type: "row" };
                    }
                }
            }
        } else if (sortType === "column") {
            for (let col = 0; col < numCols; col++) {
                for (let row = 0; row < numRows - 1; row++) {
                    if (currentMatrix[row][col] > currentMatrix[row + 1][col]) {
                        return { row1: row, row2: row + 1, col, type: "column" };
                    }
                }
            }
        } else if (sortType === "flat") {
            const flatArray = currentMatrix.flat();
            for (let i = 0; i < flatArray.length - 1; i++) {
                if (flatArray[i] > flatArray[i + 1]) {
                    const row1 = Math.floor(i / numCols);
                    const col1 = i % numCols;
                    const row2 = Math.floor((i + 1) / numCols);
                    const col2 = (i + 1) % numCols;
                    return { row1, col1, row2, col2, type: "flat" };
                }
            }
        }
        return null;
    };

    const isValidBubbleSortMove = (cell1, cell2, sortType) => {
        const expected = getNextExpectedComparison(matrix, sortType);

        if (!expected) {
            return { valid: false, reason: selLanguage?"Ο πίνακας είναι ήδη ταξινομημένος! Πάτα 'Έλεγχος Λύσης'.":"The matrix is already sorted! Press 'Check Solution'."  };
        }

        if (sortType === "row") {
            const leftCell = cell1.col < cell2.col ? cell1 : cell2;
            const rightCell = cell1.col < cell2.col ? cell2 : cell1;

            if (leftCell.row !== expected.row || leftCell.col !== expected.col1 ||
                rightCell.row !== expected.row || rightCell.col !== expected.col2) {
                return {
                    valid: false,
                    reason: selLanguage?`Λάθος! Στο Bubble Sort πρέπει να συγκρίνεις τα στοιχεία στη σειρά. Επόμενη κίνηση: Γραμμή ${expected.row}, στοιχεία [${expected.col1}] και [${expected.col2}]`:`Wrong! In Bubble Sort you must compare elements in the row. Next move: Row ${expected.row}, elements [${expected.col1}] and [${expected.col2}]`
                };
            }

            const leftValue = matrix[leftCell.row][leftCell.col];
            const rightValue = matrix[rightCell.row][rightCell.col];

            if (leftValue <= rightValue) {
                return { valid: false, reason: selLanguage?"Θυμήσου: Στον Bubble Sort εναλλάσσουμε μόνο όταν αριστερό > δεξί!":"Remember: In Bubble Sort we only swap when left > right!"  };
            }

            return { valid: true };
        }
        else if (sortType === "column") {
            const topCell = cell1.row < cell2.row ? cell1 : cell2;
            const bottomCell = cell1.row < cell2.row ? cell2 : cell1;

            if (topCell.row !== expected.row1 || topCell.col !== expected.col ||
                bottomCell.row !== expected.row2 || bottomCell.col !== expected.col) {
                return {
                    valid: false,
                    reason: selLanguage?`Λάθος! Επόμενη κίνηση: Στήλη ${expected.col}, στοιχεία [${expected.row1}] και [${expected.row2}]`:`Wrong! Next move: Column ${expected.col}, elements [${expected.row1}] and [${expected.row2}]`
                };
            }

            const topValue = matrix[topCell.row][topCell.col];
            const bottomValue = matrix[bottomCell.row][bottomCell.col];

            if (topValue <= bottomValue) {
                return { valid: false, reason:selLanguage? "Λάθος! Στο Bubble Sort εναλλάσσουμε μόνο όταν το πάνω > κάτω!" :"Wrong! In Bubble Sort we only swap when top > bottom!"  };
            }

            return { valid: true };
        }
        else if (sortType === "flat") {
            const getIndex = (cell) => cell.row * matrix[0].length + cell.col;
            const idx1 = getIndex(cell1);
            const idx2 = getIndex(cell2);

            const leftCell = idx1 < idx2 ? cell1 : cell2;
            const rightCell = idx1 < idx2 ? cell2 : cell1;

            if (leftCell.row !== expected.row1 || leftCell.col !== expected.col1 ||
                rightCell.row !== expected.row2 || rightCell.col !== expected.col2) {
                return {
                    valid: false,
                    reason: selLanguage?`Λάθος! Επόμενη σύγκριση: Θέση [${expected.row1},${expected.col1}] με [${expected.row2},${expected.col2}]`:`Wrong! Next comparison: Position [${expected.row1},${expected.col1}] with [${expected.row2},${expected.col2}]`
                };
            }

            const leftValue = matrix[leftCell.row][leftCell.col];
            const rightValue = matrix[rightCell.row][rightCell.col];

            if (leftValue <= rightValue) {
                return { valid: false, reason:selLanguage? "Λάθος! Στο Bubble Sort εναλλάσσουμε μόνο όταν το προηγούμενο > επόμενο!" :"Wrong! In Bubble Sort we only swap when previous > next!"  };
            }

            return { valid: true };
        }

        return { valid: false, reason: selLanguage?"Άγνωστος τύπος ταξινόμησης":"Unknown sort type"};
    };

    const handleCellClick = (rowIdx, colIdx) => {
        const cellId = { row: rowIdx, col: colIdx };

        if (selectedCells.length === 0) {
            setSelectedCells([cellId]);
        } else if (selectedCells.length === 1) {
            const firstCell = selectedCells[0];

            // Don't select the same cell twice
            if (firstCell.row === rowIdx && firstCell.col === colIdx) {
                setSelectedCells([]);
                return;
            }

            // Έλεγχος αν η κίνηση είναι έγκυρη σύμφωνα με το Bubble Sort
            const level = LEVELS[currentLevel];
            const validation = isValidBubbleSortMove(firstCell, cellId, level.sortType);

            if (!validation.valid) {
                setMessage(validation.reason);
                setLives(lives - 1);
                setSelectedCells([]);
                setTimeout(() => setMessage(""), 3000);
                return;
            }

            // Swap the values
            swapCells(firstCell, cellId);
            setSelectedCells([]);
            setMoves(moves + 1);
        }
    };

    const swapCells = (cell1, cell2) => {
        const newMatrix = matrix.map(row => [...row]);
        const temp = newMatrix[cell1.row][cell1.col];
        newMatrix[cell1.row][cell1.col] = newMatrix[cell2.row][cell2.col];
        newMatrix[cell2.row][cell2.col] = temp;
        setMatrix(newMatrix);
    };

    const isCellSelected = (rowIdx, colIdx) => {
        return selectedCells.some(cell => cell.row === rowIdx && cell.col === colIdx);
    };

    const checkSolution = () => {
        const level = LEVELS[currentLevel];
        let isCorrect = false;

        if (level.sortType === "row") {
            // Check if each row is sorted
            isCorrect = matrix.every(row => {
                for (let i = 0; i < row.length - 1; i++) {
                    if (row[i] > row[i + 1]) return false;
                }
                return true;
            });
        } else if (level.sortType === "column") {
            // Check if each column is sorted
            isCorrect = true;
            for (let col = 0; col < matrix[0].length; col++) {
                for (let row = 0; row < matrix.length - 1; row++) {
                    if (matrix[row][col] > matrix[row + 1][col]) {
                        isCorrect = false;
                        break;
                    }
                }
                if (!isCorrect) break;
            }
        } else if (level.sortType === "flat") {
            // Check if the entire matrix is sorted as a flat array
            const flatArray = matrix.flat();
            isCorrect = true;
            for (let i = 0; i < flatArray.length - 1; i++) {
                if (flatArray[i] > flatArray[i + 1]) {
                    isCorrect = false;
                    break;
                }
            }
        }

        if (isCorrect) {
            setMessage(selLanguage?"Σωστά! Προχωράς στο επόμενο επίπεδο!":"Correct! You advance to the next level!");
            setTimeout(() => {
                if (currentLevel < LEVELS.length - 1) {
                    setCurrentLevel(currentLevel + 1);
                } else {
                    setShowGameWon(true);
                }
            }, 1500);
        } else {
            const remLives = lives - 1;
            setLives(remLives);
            if (remLives <= 0) {
                setShowGameOver(true);
                return;
            }
            setMessage(selLanguage?"Ο πίνακας δεν είναι πλήρως ταξινομημένος ακόμα!":"Matrix is not fully sorted yet!");
            setTimeout(() => setMessage(""), 2000);
        }
    };

    const level = LEVELS[currentLevel];
    const numRows = matrix.length;
    const numCols = matrix[0]?.length || 0;

    const calculateCellSize = () => {
        const maxCells = Math.max(numRows, numCols);
        const containerWidth = Math.min(window.innerWidth - 100, 1200);
        const containerHeight = window.innerHeight - 400;

        const maxWidthPerCell = Math.floor((containerWidth - 100) / numCols);
        const maxHeightPerCell = Math.floor(containerHeight / numRows);

        let size = Math.min(maxWidthPerCell, maxHeightPerCell, 80);
        size = Math.max(size, 40);

        let fontSize = "1.8rem";
        if (size < 50) fontSize = "1rem";
        else if (size < 65) fontSize = "1.4rem";

        return { size, fontSize };
    };

    const cellConfig = calculateCellSize();

    return (
        <div
            className="main-content introDirectedContainer"
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
            />

            <div style={{ width: "100%", maxWidth: "1200px" }}>


                {/* Lives Display */}
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: 'wrap', gap: '15px' }}>

                    {/* Level Info */}
                    <div style={{
                        color: darkMode ? "white" : "#1E293B",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1E293B",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage?`Επίπεδο ${currentLevel + 1}/3`:`Level ${currentLevel + 1}/3`}
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",

                        gap: "12px"
                    }}>


                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Level Info */}
                <div className="introDirectedProm">
                    <div>
                        <p style={{ margin: "0", fontSize: "0.95rem" }}>{level.description}</p>

                    </div>
                </div>

                {/* Matrix Display with Row/Column Numbers */}
                <div className="introDirectedFrame" style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 20px",
                    overflowX: "auto",
                    overflowY: "auto"
                }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        {/* Column Numbers */}
                        <div style={{ display: "flex", marginBottom: "10px", marginLeft: `${cellConfig.size + 10}px` }}>
                            {Array.from({ length: numCols }).map((_, colIdx) => (
                                <div
                                    key={`col-label-${colIdx}`}
                                    style={{
                                        width: `${cellConfig.size}px`,
                                        marginRight: "10px",
                                        textAlign: "center",
                                        fontWeight: "700",
                                        fontSize: "1rem",
                                        color: "#3b82f6"
                                    }}
                                >
                                    {colIdx}
                                </div>
                            ))}
                        </div>

                        {/* Matrix with Row Numbers */}
                        {matrix.map((row, rowIdx) => (
                            <div key={`row-${rowIdx}`} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                {/* Row Number */}
                                <div style={{
                                    width: "50px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    fontSize: "1rem",
                                    color: "#3b82f6",
                                    marginRight: "10px"
                                }}>
                                    {rowIdx}
                                </div>

                                {/* Row Cells */}
                                <div style={{ display: "flex", gap: "10px" }}>
                                    {row.map((value, colIdx) => (
                                        <motion.div
                                            key={`${rowIdx}-${colIdx}`}
                                            onClick={() => handleCellClick(rowIdx, colIdx)}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                            style={{
                                                width: "80px",
                                                height: "80px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                fontSize: "1.8rem",
                                                fontWeight: "700",
                                                color: "white",
                                                background: isCellSelected(rowIdx, colIdx)
                                                    ? "linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                                                    : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                                borderRadius: "12px",
                                                cursor: "pointer",
                                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                                border: isCellSelected(rowIdx, colIdx) ? "3px solid #fbbf24" : "none",
                                                transition: "all 0.2s ease"
                                            }}
                                        >
                                            {value}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Message Display */}
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            style={{
                                textAlign: "center",
                                padding: "15px 20px",
                                borderRadius: "12px",
                                marginTop: "20px",
                                background: message.includes("Σωστά")
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                color: message.includes("Σωστά") ? "#059669" : "#dc2626",
                                fontWeight: "600",
                                fontSize: "1rem"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Buttons */}
                <div style={{
                    textAlign: "center",
                    marginTop: "30px",
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center"
                }}>
                    <button
                        onClick={checkSolution}
                        style={{
                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 28px",
                            fontSize: "1rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                            transition: "all 0.3s ease"
                        }}
                    >
                        {selLanguage?"Έλεγχος Λύσης":"Check Solution"}
                    </button>

                    <button
                        onClick={resetLevel}
                        style={{
                            background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 28px",
                            fontSize: "1rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 10px 25px rgba(184, 30, 30, 0.3)",
                            transition: "all 0.3s ease"
                        }}
                    >
                        {selLanguage?"Επαναφορά Επιπέδου":"Reset Level"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Sorting2D;